/**
 * selectUnsuggestedTransactions — pure Selektor für den Abgleich-Tab
 * (BR-18-A, architecture-bank-reconciliation.md §8.11).
 *
 * `buildReconcileSuggestions` (§9.2) erzeugt Vorschläge NUR für Umsätze mit
 * Match-Kandidat. Umsätze OHNE Kandidat (Stripe-Auszahlungen, Kartenzahlungen
 * an Händler ohne passende Rechnung/Ausgabe, Fremdwährungs-Umsätze) tauchten
 * dadurch im Abgleich-Tab GAR NICHT auf. Dieser Selektor liefert genau diese
 * Restmenge — komplementär zu den vier bestehenden Vorschlags-/Vorstufen-
 * Arrays (`autoSuggestions`, `reviewSuggestions`, `internalTransfers` [beide
 * Seiten], `reversals` [Debit-Seite]), damit KEIN Umsatz doppelt erscheint.
 *
 * Pure, ohne Firestore-Import (jest-jsdom-testbar ohne Emulator).
 *
 * @module bankMatching/selectUnsuggestedTransactions
 */

import { BankTransaction } from '../../types/bankTypes';

/**
 * Wählt die unmatched Umsätze OHNE Auto-/Review-Kandidat aus.
 *
 * @param unmatchedTxs - Bereits geladene Umsätze mit `matchStatus==='unmatched'`
 *   (beide Vorzeichen).
 * @param coveredTxIds - Vereinigung der txIds aus `autoSuggestions` ∪
 *   `reviewSuggestions` ∪ `internalTransfers` (BEIDE Seiten) ∪ `reversals`
 *   (Debit-Seite) — vom Aufrufer (`MatchingPanel`) aus denselben vier Arrays
 *   gebaut, die auch die bestehenden Sektionen speisen.
 * @returns `unmatchedTxs \ coveredTxIds`, sortiert nach `bookingDate` DESC,
 *   dann `id` ASC (deterministisch, testbar).
 */
export function selectUnsuggestedTransactions(
  unmatchedTxs: BankTransaction[],
  coveredTxIds: Set<string>,
): BankTransaction[] {
  return unmatchedTxs
    .filter((tx) => !coveredTxIds.has(tx.id))
    .slice()
    .sort((a, b) => {
      if (a.bookingDate !== b.bookingDate) {
        return a.bookingDate < b.bookingDate ? 1 : -1; // DESC
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
}
